// Role / people helpers. DB-side RLS is the real source of truth;
// these helpers exist so the UI can hide affordances ahead of the round-trip.
//
// Phase E (§I11): prefer JWT-claim permission checks
// (`hasPermission`, `canAdmin`, etc.) over role-name matching.
// Legacy `userIsAdmin` / `userIsSuperadmin` kept as the second leg of
// a union check during the dual-read migration window — matches the
// governance SQL side (see 0067_permission_based_rls.sql).

// Bare shared-library import, not a deep peopleUtils.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0). Deferred-call
// wrapper, not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellFindPerson = (...args) => _wsShared.functions.findPerson(...args)
import { shellResolveClaims } from './claimResolver.js'

// ── Phase E JWT-claim readers ────────────────────────────────────
// Live claim name is `permissions` (matches `symbols-auth-bridge` +
// `jwt_has_perm` helper). TASKS_NEEDED §62 mentions `active_permissions`
// — that's documentation drift; code authoritative.

// Capstone GATE A: route every claim read through `resolveClaims` so the
// WHOLE module reads consistent, Mongo `/auth/me`-sourced primitives. See
// claimResolver.js.
const _claims = (root) => shellResolveClaims(root)
export const currentPermissions = (root) => _claims(root).permissions || []

// True if the caller has the given permission key OR is an owner.
// Owner shortcut mirrors the SQL helper's owner-bypass.
function shellHasPermission(root, perm) {
  const r = root != null ? root : this && this.state && this.state.root
  if (!r) return false
  if (_claims(r).is_owner) return true
  return currentPermissions(r).includes(perm)
}
// Registered shell-prefixed (shellHasPermission, shellCanAdmin, …): shared owns
// the bare names (shared/functions/memberProfileAccess.js) and hands the call
// to these inside the shell, where the team-permission union below exists.
export { shellHasPermission }

// `root` is the roster to read: a helper in this file passes the root it
// already holds, and `el.call('hasRole', user, role)` leaves it out, so
// `this.state.root` serves. The helpers below used to call `hasRole(user, …)`
// with no `this` and no root, so `roles.byUser` never counted and only the
// legacy per-person array decided (P5 finding: shellIsSuperAdmin,
// currentUserIsAdmin and every gate below).
export function hasRole(user, role, root) {
  // CU-10 §90: roles are no longer on the person object. They live in
  // root.roles.byUser[userId]. We check that instead.
  if (!user) return false
  // Legacy path: if person still has a .roles array (pre-migration client),
  // honour it during the transition window.
  if (Array.isArray(user.roles) && user.roles.length > 0) {
    return user.roles.includes(role)
  }
  // New path: look up the userId in root.roles.byUser.
  const r = root != null ? root : this && this.state && this.state.root
  if (!r || !user.userId) return false
  const assignedRole = (r.roles?.byUser || {})[user.userId]
  return assignedRole === role
}

export function userIsSuperadmin(user, root) {
  return hasRole.call(this, user, 'superadmin', root)
}

export function userIsAdmin(user, root) {
  return hasRole.call(this, user, 'superadmin', root) || hasRole.call(this, user, 'admin', root)
}

export function isExternalEmail(email, people = []) {
  if (!email) return true
  return !people.find((p) => p.email === email)
}

export function currentUser(root) {
  const r = root != null ? root : this && this.state && this.state.root
  return r ? shellFindPerson(r, r.currentUserEmail) : null
}

export function currentUserIsAdmin(root) {
  const r = root != null ? root : this && this.state && this.state.root
  if (!r) return false
  // JWT-based ownership trumps the Mongo `roles.byUser` lookup. The auth
  // gates on /admin/* pages use JWT claims (isOwner / org_role / perms),
  // so the dropdown discovery link must follow the same rule — otherwise
  // legitimate owners can reach the pages directly but never see the
  // entry point. Mongo `roles.byUser` is org-membership-scoped and not
  // always seeded for owners on every org.
  const c = _claims(r)
  if (c?.is_owner === true) return true
  if (_isOwnerLikeRole(c?.org_role)) return true
  if (_isOwnerLikeRole(c?.workspace_role)) return true
  if (c?.symbols_role === 'admin' || c?.symbolsRole === 'admin') return true
  // Mongo-derived org role from `root.org.role` (active-org OrgMember
  // role). Canonical source; bypasses stale federated JWTs per
  // SERVER-AUTH-ORGS-DRIFT-1 in tickets/server.md.
  if (_isOwnerLikeRole(r.org?.role)) return true
  return userIsAdmin(currentUser(r), r)
}

export function currentUserIsSuperadmin(root) {
  const r = root != null ? root : this && this.state && this.state.root
  return userIsSuperadmin(currentUser(r), r)
}

// ── el.call()-friendly permission checks ──────────────────────────
// Via el.call: el.call('isAdmin') — no args needed, root from this.state.root.
// Via direct: isAdmin(s) — pass component state, root from s.root.
// All use regular function (not arrow) so `this` binds when called via el.call.

export function isAdmin(s) {
  const root = s?.root || (this && this.state && this.state.root)
  return userIsAdmin(currentUser(root), root)
}

// Walk up the element tree to the nearest ancestor with a `routes` map —
// the page-level router root that `el.router(path, root)` needs.
export function findRouteRoot(startEl) {
  let cur = startEl || this
  while (cur && !cur.routes) cur = cur.parent
  return cur || null
}

// WS-REDIRECT-1 (tickets/opus.md) — the "app silently navigates itself away"
// class. Root cause: every redirect gate below is re-evaluated on BOTH
// mount (onInit) AND every subsequent reactive render (onUpdate — see
// workspaceProjects.js `_guardPage`), and each gate's own "cold-load-safe"
// bail only checks that `root.people`/claims are NON-EMPTY, not that they
// describe the CURRENTLY ACTIVE org/workspace. During an org switch
// (shared/functions/switchers/switchOrg.js) `root.org` is nulled
// synchronously while `root.people` — never cleared by that pipeline — keeps
// serving the PREVIOUS org's roster for the whole async round-trip
// (getOrganization + listWorkspaces + switchWorkspace, each observed
// 1-3s), and `claimResolver.js`'s `_activeOrg` falls back to
// `root.jwtClaims.workspace_org_id` (also stale) whenever `root.org` is
// null. So a page that re-renders mid-switch computes a permission decision
// against the OUTGOING org/workspace's data and can bounce a fully
// permitted user to `/` — which, in turn, resolves against whatever
// org/workspace the router wrapper reads at THAT exact tick, landing on
// unrelated routes (the reported `/w/system/hq/chat` → `/w/system/hq`
// cycle, and the `/chat` → `/calendar` → spinner cycle on a cold boot's
// equivalent hydration window). `_wsSwitching` / `_wpSwitching` /
// `_dataLoading` are the SAME "scope not settled" flags `_isLoaderActive`
// (workspace/app.js) already gates the MainLoader on for this exact
// window — a redirect decision made while any of them is up is reading a
// scope in flux, not a resolved one, so every gate below must wait for the
// same settle signal instead of treating "non-empty" as "current".
const _scopeConverging = (root) =>
  !!(root?._wsSwitching || root?._wpSwitching || root?._dataLoading)

// Cold-load-safe admin gate. Returns true when the page should redirect.
// Bails (returns false) until both `currentUserId` and the `people` row set
// are seeded — otherwise findPerson would falsely report "not admin"
// during initial hydration and bounce the user away. Also bails while the
// active org/workspace scope is still converging (see `_scopeConverging`
// above) — a genuinely non-admin user gets re-checked the moment the
// switch/boot settles, so nothing real is granted, just deferred past the
// window where the read is unreliable.
export function shouldRedirectNonAdmin(s, role) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root?.currentUserId) return false
  if (_scopeConverging(root)) return false
  if (!Array.isArray(root.people) || !root.people.length) return false
  const checker = role === 'superadmin' ? userIsSuperadmin : userIsAdmin
  // Phase E union: permission-matching leg first so JWT-carried owners
  // and perm-granted users pass without needing a Supabase `user_roles`
  // row. Legacy role check remains as the second leg during the
  // dual-read window.
  const permKey = role === 'superadmin' ? 'admin.danger.access' : 'admin.pages.read'
  if (shellHasPermission(root, permKey)) return false
  return !checker(currentUser(root), root)
}

// Phase E — permission-gated page guard. Same cold-load semantics as
// `shouldRedirectNonAdmin` but checks a specific permission key instead
// of role names. Preferred going forward.
//
// Recognises FIVE owner/admin signals, in order from strongest to
// weakest. The federated Supabase JWT can drift (SERVER-AUTH-ORGS-DRIFT-1
// in tickets/server.md — `app_metadata.is_owner` / `org_role` may not
// reflect the user's actual `OrgMember.role` if the bridge mint races
// the membership write), so we ALSO consult Mongo-derived `root.org.role`
// before falling back to the people-roles legacy leg. Otherwise users
// with valid Mongo OrgMember rows but a stale JWT get bounced from the
// admin pages they own.
const _isOwnerLikeRole = (r) => r === 'owner' || r === 'admin'

// (1)–(4) of `shouldRedirectNoPerm`: the signals that keep the page AT ONCE,
// before the roster or the org role map has landed. `permGateAnswer` reads
// the same legs, so the two can never disagree.
const _permOpensEarly = (root, perm) => {
  const claims = _claims(root)
  // (1) Federated JWT — fastest path when claims are fresh.
  if (claims.is_owner) return true
  if (_isOwnerLikeRole(claims.org_role)) return true
  if (_isOwnerLikeRole(claims.workspace_role)) return true
  // (2) Symbols-level super-admin (`symbols_role: 'admin'`). Bypasses
  //     org gates because it's a platform-wide privilege.
  if (claims.symbols_role === 'admin' || claims.symbolsRole === 'admin') return true
  // (3) Specific permission key on the JWT.
  if (shellHasPermission(root, perm)) return true
  // (4) Mongo source of truth — `root.org.role` is the active-org's
  //     `OrgMember.role`, populated by `subscribeAuthSession` from
  //     `/core/auth/me`'s `user.organizations[*].role`. This is the
  //     canonical source and bypasses stale federated claims.
  return _isOwnerLikeRole(root.org?.role)
}

// What the final legacy leg (5) waits for: the roster, and — for the two keys
// it maps to a role — the org member-role map.
// (4b) The legacy leg reads the org member-role map (`roles.byUser`, through
//     `hasRole`), and the boot lands that map LAST: loadPrivateData
//     `_loadRoles` runs in the non-blocking tail, after the roster. On a cold
//     deep link the roster is in and the map is not; read then, the leg finds
//     no role and sends away a person whose admin role lives only in the map —
//     for good, since `routeGateBounce` sends a page away once. So the two
//     keys the leg maps to a role wait for the map: the boot declares it at
//     `version` 0, a workspace switch resets it to null, and every
//     `_loadRoles` write moves the version — a failed read included, so the
//     wait always ends (functions/roleMapGate.test.js).
const _permLegacyWaits = (root, perm) =>
  !Array.isArray(root.people) ||
  !root.people.length ||
  ((perm === 'admin.pages.read' || perm === 'admin.danger.access') &&
    !(root.roles && root.roles.version > 0))

export function shouldRedirectNoPerm(s, perm) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root?.currentUserId) return false
  // See `_scopeConverging`'s header comment above (WS-REDIRECT-1) — this
  // gate is re-run on every reactive render (workspaceProjects.js
  // `_guardPage`'s onUpdate re-check, plus admin.js's own onInit), so an
  // org/workspace switch mid-flight must not be read as a settled "no
  // permission" answer.
  if (_scopeConverging(root)) return false
  if (_permOpensEarly(root, perm)) return false
  if (_permLegacyWaits(root, perm)) return false
  // (5) Final legacy leg — `admin.pages.read` maps to admin role on
  //     the people row; explicit perms not in the claim fall through
  //     so users with `user_roles` entries but no Phase E JWT don't
  //     lose access.
  if (perm === 'admin.pages.read') return !userIsAdmin(currentUser(root), root)
  if (perm === 'admin.danger.access') return !userIsSuperadmin(currentUser(root), root)
  return true
}

export function shellIsSuperAdmin(s) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root) return false
  // Symbols-platform super-admin claim trumps the Mongo `roles.byUser`
  // legacy lookup. `symbols_role: 'admin'` (or camelCase `symbolsRole`)
  // is the platform-level super-role and bypasses org/workspace gates.
  const c = _claims(root)
  if (c?.symbols_role === 'admin' || c?.symbolsRole === 'admin') return true
  return userIsSuperadmin(currentUser(root), root)
}

// Cold-load-safe STRICT superadmin gate. Returns true when the page
// should redirect a non-superadmin away. The canonical access check
// for /admin and every /admin/* surface — owners and the `admin` role
// are LOCKED OUT. Only people with the explicit `superadmin` role on
// the people roster, OR the Symbols-platform admin claim
// (`symbols_role: 'admin'`), pass.
//
// Bails (returns false) during initial hydration when either the user
// id or the `people` row set is missing — otherwise the gate would
// falsely report "not superadmin" before the people fetch resolves and
// bounce a legitimate superadmin to /.
export function shouldRedirectNonSuperadmin(s) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root?.currentUserId) return false
  // See `_scopeConverging`'s header comment above (WS-REDIRECT-1) — bail
  // while an org/workspace switch is mid-flight rather than reading a
  // scope in flux as a settled "not superadmin" answer.
  if (_scopeConverging(root)) return false
  // Platform-level claim is the fast path — Symbols staff don't need
  // a Mongo people row to administer.
  const c = _claims(root)
  if (c?.symbols_role === 'admin' || c?.symbolsRole === 'admin') return false
  // People-roster check needs the roster hydrated.
  if (!Array.isArray(root.people) || !root.people.length) return false
  return !userIsSuperadmin(currentUser(root), root)
}

// The caller's role on the ACTIVE workspace, as the server resolves it — the
// role the workspace settings writers check. WorkspaceController's
// `updateWorkspace` (the name) and `updateWorkspaceSettings` (every settings
// key) both run `requireWorkspaceRole(req, res, workspaceId, ['owner', 'admin'])`
// over `AccessResolver.resolveWorkspaceRole(userId, workspaceId)`, which
// resolves in this order:
//   1. a direct WorkspaceMember row decides — its role, normalized
//      (`normalizeWorkspaceMemberRole`). The org role is not read then: an org
//      admin named `editor` on the workspace is an editor there, and the
//      writers answer 403;
//   2. no row, and the workspace is not `visibility: 'private'`: the OrgMember
//      role cascades (`mapCascadingOrgRole` — owner → owner; co-owner, admin,
//      maintainer → admin; member → editor; contributor → nothing);
//   3. else the best TeamWorkspaceAccess grant; else no role (a 404).
// The shell holds neither the direct row nor the team grants, so it cannot
// run that resolution itself, and the org role alone answers wrong for (1).
// It does not have to: the workspace READ (`getWorkspace`) is gated on the
// same call and hands its answer back as `workspace.access.role`
// (`callerAccess`); the boot merges it into the held record in the
// background. This reads THAT answer and nothing else — no org-role fallback
// and no token claim (the JWT's `workspace_role` has no minter): either one
// would grant past the server.
//
// Answers the role string, or undefined while it is not known: no identity
// yet, an org/workspace switch in flight (`_scopeConverging`), no record, or a
// record whose `access` has not landed. A caller WAITS on undefined — a route
// gate does not bounce, a write control does not show.
export function callerWorkspaceRole(root) {
  const r = root != null ? root : this && this.state && this.state.root
  if (!r || !r.currentUserId || _scopeConverging(r)) return undefined
  const access = r.workspace && r.workspace.access
  const role = access && typeof access === 'object' ? access.role : undefined
  return typeof role === 'string' && role ? role : undefined
}

// The root-state keys a ROUTE GATE reads — the inputs of every gate above:
// identity (`currentUserId`, `currentUserEmail`, `user`), the claims and the
// active org (`jwtClaims`, `org`, `orgs` — claimResolver.js), the roster
// (`people`, then `peopleAll` — shared peopleUtils `findPerson`), the org
// member-role map (`roles` — `hasRole`'s `roles.byUser` leg, read whenever a
// caller hands it the root or an element `this`; `_loadRoles` fills it in the
// boot's non-blocking tail, AFTER the roster, so it is the input that lands
// last), the workspace role the server resolved
// (`workspace.access`, `callerWorkspaceRole`) and the three "scope not
// settled" flags (`_scopeConverging`). A gated page declares
// `subscribeTo: (el) => el.call('routeGateKeys')`.
//
// Why a page needs it (F2, 2026-10-31): on a cold deep link these land AFTER
// the page's onInit — identity after the auth tick, `access` after the boot's
// background workspace read (shared bootResolve.js "the caller's access") —
// and smbls runs a page's update hooks on a root-state write ONLY for the keys
// the page subscribes to (@symbo.ls/element registerSubscribeToEffects; the
// root store's own update reaches the store owner, the app element, and
// nothing below it). Without the subscription the gate ran once, at onInit,
// on an unsettled scope, waited — and never ran again: an editor kept every
// /admin page they deep-linked into (dev W31, QA employee and pm).
//
// The subscription pass re-runs the GATE ALONE: the page's onBeforeUpdate
// answers `false` for `{ updateBySubscription: true }` after it ran the gate,
// which vetoes the rest of that pass (the forced prop re-runs and onUpdate),
// so no other page work runs more often than before. The gate bounces through
// `routeGateBounce` below.
export function routeGateKeys() {
  return [
    'currentUserId',
    'currentUserEmail',
    'user',
    'jwtClaims',
    'org',
    'orgs',
    'people',
    'peopleAll',
    'roles',
    'workspace.access',
    '_wsSwitching',
    '_wpSwitching',
    '_dataLoading'
  ]
}

// Send a gated page's viewer to `path`, ONCE per mounted page. Call it with
// the page element as `this`: `el.call('routeGateBounce', '/')`.
//
// Once: the subscription above wires one effect PER KEY, so a single store
// write that lands several gate inputs (shared authBridge.js writes
// `currentUserEmail`, `currentUserId` and `jwtClaims` in one update) re-runs
// the gate once per key in the same flush. The leaving page stays mounted until the router swaps it out — it
// first awaits the shell's exit motion (app.js `onRouteExit`, up to 300 ms) —
// so without the latch every one of those passes navigated again, one history
// entry each (the house idiom for "forward once": pages/workspaceHub.js
// `__wHubForwarded`). A navigation mounts a new page element, so the latch
// starts clear on every visit.
//
// The router root: `findRouteRoot` answers the app ELEMENT that carries
// `routes`; `getRoot()` is the fallback for a tree with none.
export function routeGateBounce(path) {
  const el = this
  if (!el || typeof el.router !== 'function' || el.__routeGateBounced) return
  el.__routeGateBounced = true
  const routeRoot = el.call('findRouteRoot', el) || el.getRoot()
  el.router(path || '/', routeRoot, {}, { pushState: true })
}

export function isManager(s) {
  const root = s?.root || (this && this.state && this.state.root)
  const user = currentUser(root)
  return hasRole(user, 'manager', root) || userIsAdmin(user, root)
}

// Phase E — permission-gated counterparts. Prefer these going forward;
// the `userIsAdmin`/`userIsSuperadmin` variants remain for the dual-read
// window so callers don't all migrate in one commit.
export function shellCanAdmin(s) {
  const root = s?.root || (this && this.state && this.state.root)
  return shellHasPermission(root, 'admin.pages.read') || userIsAdmin(currentUser(root), root)
}

export function shellCanSuperadmin(s) {
  const root = s?.root || (this && this.state && this.state.root)
  return (
    shellHasPermission(root, 'admin.danger.access') || userIsSuperadmin(currentUser(root), root)
  )
}

export function canManage(s) {
  const root = s?.root || (this && this.state && this.state.root)
  if (shellHasPermission(root, 'member.update_role')) return true
  const user = currentUser(root)
  return hasRole(user, 'manager', root) || userIsAdmin(user, root)
}

// Can view private data (equity, docs, salary) for a given person.
// Phase E: self OR `profile.private.read` perm OR legacy superadmin.
// el.call('canAccessPrivate', person) — shared's, which hands it here in the shell
export function shellCanAccessPrivate(sOrPerson, maybePerson) {
  let root, person
  if (maybePerson !== undefined) {
    root = sOrPerson?.root || (this && this.state && this.state.root)
    person = maybePerson
  } else {
    root = this && this.state && this.state.root
    person = sOrPerson
  }
  if (!root) return true
  const isSelfUser = !person?.email || person.email === root.currentUserEmail
  if (isSelfUser) return true
  if (shellHasPermission(root, 'profile.private.read')) return true
  return userIsSuperadmin(currentUser(root), root)
}

// Can edit a person's profile fields.
// Phase E: self OR `profile.edit_others` perm OR legacy superadmin.
// el.call('canEditProfile', person) — shared's, which hands it here in the shell
export function shellCanEditProfile(sOrPerson, maybePerson) {
  let root, person
  if (maybePerson !== undefined) {
    root = sOrPerson?.root || (this && this.state && this.state.root)
    person = maybePerson
  } else {
    root = this && this.state && this.state.root
    person = sOrPerson
  }
  if (!root) return false
  const isSelfUser = !person?.email || person.email === root.currentUserEmail
  if (isSelfUser) return true
  if (shellHasPermission(root, 'profile.edit_others')) return true
  return userIsSuperadmin(currentUser(root), root)
}

// True when the current user is a signed-in core team member.
// Used by AppHeader to show/hide the cross-app nav.
export function isActiveMember(s) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root || !root.signedIn) return false
  const people = root.people || []
  const email = root.currentUserEmail
  if (!email || !people.find((p) => p.email === email)) return false
  // CU-10 §90: roles.available dropped with public.roles view (migration 0087).
  // Any signed-in org member is considered active regardless of role.
  return true
}

// Group a list of people by their role. Returns an ordered array of
// { role, label, color, users } objects. Available via el.call('groupByRole', people).
export function groupByRole(people) {
  const root = this && this.state && this.state.root
  const available = root?.roles?.available || []
  // CU-10 §90: role from root.roles.byUser[userId]; fallback to p.roles for legacy data.
  const byUser = root?.roles?.byUser || {}
  const groups = {}
  for (const p of people || []) {
    const legacyRoles = Array.isArray(p.roles) && p.roles.length > 0 ? p.roles : null
    const role = legacyRoles ? legacyRoles[0] : byUser[p.userId] || 'member'
    if (!groups[role]) groups[role] = []
    groups[role].push(p)
  }
  // Order by the roles enum order, then any extras
  const ordered = available.length
    ? available.map((r) => r.name)
    : ['superadmin', 'admin', 'manager', 'member', 'advisor', 'investor']
  return ordered
    .filter((r) => groups[r]?.length)
    .map((r) => {
      const meta = available.find((m) => m.name === r) || {}
      return {
        role: r,
        label: meta.label || r.replace(/^./, (c) => c.toUpperCase()),
        color: meta.color || 'gray',
        users: groups[r].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      }
    })
}

// A person's monogram letters — the first letter of the first two words of the
// name, upper-cased. ONE definition for both person mappers: the shell's
// (loadPrivateData._person, which reaches it through the functions barrel)
// and the page boilerplate's (rowToPerson below). It lived in the barrel, and
// rowToPerson never set the field — so after any page with the boilerplate
// fetch (/admin/system, /features, /inbox …) rewrote root.people, every member
// without a photo showed an EMPTY monogram on /admin (live on dev, 2026-09-24).
export const initials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()

export const rowToPerson = (r) => ({
  // user_id MUST be carried through — MemberProfile's _patchUser bails out
  // (`if (!person?.userId) return`) when it's missing, silently dropping every
  // Settings save. This mapper feeds root.people via the page boilerplate
  // fetch (calendar/chat/meet/tickets/features), so without userId, opening
  // /settings after any of those pages makes profile edits no-op. Keep this
  // in sync with loadPrivateData._person (the shell's mapper, which already
  // includes userId). Fixes "Setting information is not saved".
  userId: r.user_id,
  name: r.name,
  email: r.email,
  avatar: r.avatar,
  // The monogram letters, as the shell's own mapper carries them
  // (loadPrivateData._person): the photo field is a URL or nothing.
  initials: initials(r.name),
  position: r.position,
  location: r.location,
  country: r.country,
  city: r.city,
  timezone: r.timezone,
  github: r.github || r.github_username,
  weeklyHours: r.weekly_hours,
  workingDays: r.working_days,
  workingHours: { start: r.working_hours_start, end: r.working_hours_end },
  // CU-10 §90: roles/isAdmin removed from people view. Role data lives in
  // root.roles.byUser[userId] (server GET /auth/org/:orgId/member-roles).
  origin_valuation: r.origin_valuation
})

// ── The /admin pages' own gates, one answer per page (3a) ────────────────────
//
// Each /admin page sends away a person whose role does not open it. The admin
// rail (functions/adminRail.js), the /admin landing's forward and the account
// menu's Admin entry ask the SAME question, so they list exactly the pages a
// person's role opens (concierge decision 2026-10-31 23:35, 3a). An answer is
// 'open' (the page keeps the person, for good), 'closed' (the page sends the
// person away) or 'pending' (the page waits: an input its gate reads has not
// landed yet — identity, a scope in flux, the roster, the org role map, the
// org role or the workspace role).

// `shouldRedirectNoPerm` as one of the three answers: 'closed' exactly when it
// sends the person away; it reads the same legs (`_permOpensEarly`,
// `_permLegacyWaits`).
export function permGateAnswer(s, perm) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root?.currentUserId || _scopeConverging(root)) return 'pending'
  if (_permOpensEarly(root, perm)) return 'open'
  if (_permLegacyWaits(root, perm)) return 'pending'
  return shouldRedirectNoPerm.call(this, s || { root }, perm) ? 'closed' : 'open'
}

// The gate of /admin/workspace-settings, /admin/dashboard-defaults and
// /admin/module: the server's writes behind them take a workspace owner or
// admin (`callerWorkspaceRole` above has the resolution); it waits while that
// role has not landed.
export function workspaceAdminGateAnswer(root) {
  const role = callerWorkspaceRole.call(this, root)
  if (role === undefined) return 'pending'
  return role === 'owner' || role === 'admin' ? 'open' : 'closed'
}

// The gate of /admin/domains, /admin/branding and /admin/mail: the org's own
// settings, for its owners and admins (the active org's `OrgMember.role`); it
// waits while identity or that role has not landed.
export function orgAdminGateAnswer(root) {
  const r = root != null ? root : this && this.state && this.state.root
  const role = r && r.currentUserId && r.org && r.org.role
  if (!role) return 'pending'
  return ['owner', 'co-owner', 'admin', 'maintainer'].indexOf(role) >= 0 ? 'open' : 'closed'
}

// The gate of /admin/data (the workspace data export): an owner or admin of
// THIS workspace. The org role cascades onto the workspace on the server (an
// org owner, co-owner or admin is a workspace owner or admin there); the
// session claim carries the direct workspace role. It waits while neither
// role has landed.
export function dataExportGateAnswer(root) {
  const r = root != null ? root : this && this.state && this.state.root
  const claims = (r && r.jwtClaims) || {}
  const orgRole = r && r.org && r.org.role
  if (!(r && r.currentUserId && (orgRole || claims.workspace_role))) return 'pending'
  if (orgRole === 'owner' || orgRole === 'co-owner' || orgRole === 'admin') return 'open'
  if (claims.is_owner) return 'open'
  return claims.workspace_role === 'owner' || claims.workspace_role === 'admin' ? 'open' : 'closed'
}

// `shouldRedirectNonAdmin` (the /admin/flags gate) as one of the three
// answers: it waits for identity, a settled scope and the roster, then asks
// for an admin.
export function adminRoleGateAnswer(s) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root?.currentUserId || _scopeConverging(root)) return 'pending'
  if (!Array.isArray(root.people) || !root.people.length) return 'pending'
  return shouldRedirectNonAdmin.call(this, s || { root }) ? 'closed' : 'open'
}

// The gate table of the /admin pages, one entry per route of the admin rail
// (the rail's href, no workspace prefix): the gate that page runs. A fresh
// table on every call; the table lives inside the function that returns it.
export function adminPageGates() {
  return {
    '/admin': ['perm', 'admin.pages.read'],
    '/admin/workspace-settings': ['workspace'],
    '/admin/teams': ['perm', 'admin.team_permissions'],
    '/admin/permissions': ['perm', 'admin.team_permissions'],
    '/admin/dashboard-defaults': ['workspace'],
    '/admin/domains': ['org'],
    '/admin/branding': ['org'],
    '/admin/data': ['export'],
    '/admin/danger': ['perm', 'admin.danger.access'],
    '/admin/usage': ['perm', 'admin.pages.read'],
    '/admin/analytics': ['perm', 'admin.pages.read'],
    '/admin/logs': ['perm', 'admin.pages.read'],
    '/admin/activity-scoring': ['perm', 'admin.pages.read'],
    '/admin/integrations': ['perm', 'admin.pages.read'],
    '/admin/mail': ['org'],
    '/admin/module': ['workspace'],
    '/admin/flags': ['admin'],
    '/admin/status': ['perm', 'admin.pages.read'],
    '/admin/system': ['perm', 'admin.pages.read'],
    '/data/external': ['none']
  }
}

// One page's answer, by its route (the rail's href, no workspace prefix): the
// gate that page runs (`adminPageGates`). The pages with a gate of their own
// call it with their own route, so the page and the rail cannot drift; the
// pages on `shouldRedirectNoPerm` name the same key here (pages/admin/
// adminPageAccess.test.js holds both). A route with no gate of its own
// (/data/external) answers 'open'; a route this table does not know answers
// undefined.
export function adminPageAccess(root, href) {
  const r = root != null ? root : this && this.state && this.state.root
  const s = { root: r }
  const gate = adminPageGates()[String(href || '').split('?')[0]]
  if (!gate) return undefined
  if (gate[0] === 'none') return 'open'
  if (gate[0] === 'perm') return permGateAnswer.call(this, s, gate[1])
  if (gate[0] === 'workspace') return workspaceAdminGateAnswer.call(this, r)
  if (gate[0] === 'org') return orgAdminGateAnswer.call(this, r)
  if (gate[0] === 'export') return dataExportGateAnswer.call(this, r)
  return adminRoleGateAnswer.call(this, s)
}

// The admin AREA's answer (3c): 'open' when the person's role opens at least
// one /admin page, 'pending' while none opens yet and one still waits for its
// inputs, 'closed' when every /admin page sends the person away. The
// launcher's Admin group asks it (functions/searchApps.js, which runs with no
// element to ask); adminRail.js `canOpenAdmin`, the account menu's Admin
// entry, reads the same answers over the rail's rows, the same routes
// (functions/launcherAdminGroup.test.js holds the two together).
export function adminAreaAccess(root) {
  const r = root != null ? root : this && this.state && this.state.root
  const answers = Object.keys(adminPageGates())
    .filter((href) => href.indexOf('/admin') === 0)
    .map((href) => adminPageAccess.call(this, r, href))
  if (answers.indexOf('open') >= 0) return 'open'
  return answers.indexOf('pending') >= 0 ? 'pending' : 'closed'
}
